import {observe} from '@github-ui/selector-observer'

const DASH = '—'

/**
 * Returns the local-time ISO-8601 timestamp
 * truncated to seconds.
 *
 * @example
 * // If your machine is on UTC+01:00
 * toLocalIso(new Date('2025-07-31T15:45:00Z'))
 * // → "2025-07-31T16:45:00"
 */
function toLocalIso(date: Date): string {
  const MILLISECONDS_PER_MINUTE = 60_000
  return new Date(date.getTime() - date.getTimezoneOffset() * MILLISECONDS_PER_MINUTE).toISOString().slice(0, 19)
}

/**
 * Parses the raw value string from a date picker into a Date object or null.
 *
 * Behavior:
 *   - Returns `null` for empty values or when the literal string "undefined" is encountered.
 *     (Some browsers have been observed to emit "undefined" when the picker is cleared.)
 *   - If the value is a finite number, it is treated as a timestamp (milliseconds since epoch).
 *   - Otherwise, attempts to parse the string with `Date.parse`.
 *
 * @param val Raw string value from a date picker input
 * @returns {Date | null} A Date object if parsing succeeds, or null if the value is empty/invalid
 */
function parsePickerValue(val: string): Date | null {
  if (!val || val === 'undefined') return null
  const num = Number(val)
  if (Number.isFinite(num)) return new Date(num)

  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const parts = val.split('-').map(Number)
    if (parts.length === 3 && parts.every(p => !Number.isNaN(p))) {
      return new Date(parts[0]!, parts[1]! - 1, parts[2])
    }
  }

  const parsed = Date.parse(val)
  return Number.isNaN(parsed) ? null : new Date(parsed)
}

function convertTo24Hour(hour: number, ampm: string): number {
  if (ampm === 'pm' && hour < 12) return hour + 12
  if (ampm === 'am' && hour === 12) return 0
  return hour
}

function buildSummary({
  date,
  hour,
  minute,
  ampm,
  tzLabel,
}: {
  date: Date | null
  hour: string
  minute: string
  ampm: string
  tzLabel: string
}): string {
  const datePart = date ? date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric'}) : DASH
  const hr = hour || DASH
  const min = minute || DASH
  const am = hour && minute ? ` ${ampm.toUpperCase()}` : ''
  return `${datePart}, ${hr}:${min}${am} ${tzLabel || DASH}`
}

function validateSchedule({
  date,
  hour,
  minute,
  ampm,
  now,
}: {
  date: Date | null
  hour: string
  minute: string
  ampm: string
  now: Date
}): {ok: boolean; message: string} {
  if (!date) return {ok: false, message: 'Please pick a date.'}
  if (!hour || !minute || Number.isNaN(Number(hour)) || Number.isNaN(Number(minute))) {
    return {ok: false, message: 'Please choose both hour and minute.'}
  }

  const h24 = convertTo24Hour(Number(hour), ampm)

  const chosen = new Date(date)
  chosen.setHours(h24, Number(minute), 0, 0)
  if (chosen <= now) return {ok: false, message: 'Scheduled time must be in the future.'}

  return {ok: true, message: ''}
}

const notBlank = (str: string | undefined | null): boolean => !!str && str.trim().length > 0

/**
 * Returns true if the timezone label appears to be set.
 *
 * Expected formats:
 *   - "GMT" or "UTC" (case-insensitive)
 *   - Numeric offsets such as "+02:00", "-0500", or "+2"
 *   - IANA timezone names like "Europe/London" or "America/New_York"
 *
 * Notes:
 *   - This does not validate short abbreviations ("PST", "CET").
 */
function tzIsSet(label: string): boolean {
  if (!label) return false
  const lower = label.trim().toLowerCase()
  if (lower.includes('not set')) return false
  return /^(gmt|utc|[+-]\d{1,2}:?\d{2}|[a-z]+\/[a-z_]+)/i.test(lower)
}

function readyToSchedule(opts: {title: string; body: string; scheduleOk: boolean; tzSet: boolean}): boolean {
  return notBlank(opts.title) && notBlank(opts.body) && opts.scheduleOk && opts.tzSet
}

/**
 * Helper function to get cookie value by name
 */
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

/**
 * Gets the user's timezone with fallbacks:
 * 1. First tries to get from 'tz' cookie (URL decoded)
 * 2. Falls back to Intl API detection
 * 3. Falls back to GMT offset calculation
 */
function getTimezone(): string {
  // Try to get timezone from cookie first
  const cookieTz = getCookie('tz')
  if (cookieTz) {
    try {
      // URL decode the timezone (e.g., "Europe%2FLondon" → "Europe/London")
      return decodeURIComponent(cookieTz)
    } catch {
      // If decoding fails, fall through to other methods
    }
  }

  // Fallback 1: Try to get from Intl API
  try {
    const intlTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (intlTz) return intlTz
  } catch {
    // Intl API not available, fall through
  }

  // Fallback 2: Calculate GMT offset
  const offset = -new Date().getTimezoneOffset() / 60
  const sign = offset >= 0 ? '+' : '-'
  const hours = Math.abs(offset)
  return `GMT${sign}${hours}`
}

/**
 * Formats timezone for display in UI
 * - Extracts city name from IANA timezones (e.g., "London" from "Europe/London")
 * - Returns GMT offsets as-is
 * - Handles other formats gracefully
 */
function formatTimezoneForDisplay(tz: string): string {
  // If it's an IANA timezone (contains slash), extract the city name
  if (tz.includes('/')) {
    return tz.split('/').pop()?.replace(/_/g, ' ') || tz
  }

  if (tz.startsWith('GMT') || /^[+-]\d{1,2}:?\d{0,2}$/.test(tz)) {
    return tz
  }

  return tz
}

function attachScheduler(dialog: HTMLElement) {
  // Query for both the visible date input and the hidden timestamp input
  const dateInput = dialog.querySelector<HTMLInputElement>('input.js-discussion-due-date[type="date"]')!
  const _timestampInput = dialog.querySelector<HTMLInputElement>('input.js-discussion-timestamp[type="hidden"]')!

  const hourSel = dialog.querySelector<HTMLSelectElement>('#js-hour')!
  const minuteSel = dialog.querySelector<HTMLSelectElement>('#js-minute')!
  const ampmSel = dialog.querySelector<HTMLSelectElement>('#js-ampm')!
  const summary = dialog.querySelector<HTMLElement>('.js-schedule-summary')!
  const scheduleBtn = dialog.querySelector<HTMLButtonElement>('#js-schedule-btn')!
  const titleInput = dialog.closest('form')!.querySelector<HTMLInputElement>('#js-discussion-title')!
  const bodyInput = dialog.closest('form')!.querySelector<HTMLTextAreaElement>('#js-discussion-body')!

  // Get the existing timezone label element and its current text
  const tzLabelElement = dialog.querySelector<HTMLElement>('.js-timezone-label')
  const existingTzLabel = tzLabelElement?.textContent?.trim() || ''

  // Determine which timezone to use for display and validation
  let displayTimezone: string
  let validationTimezone: string

  if (tzIsSet(existingTzLabel)) {
    displayTimezone = existingTzLabel
    validationTimezone = existingTzLabel
  } else {
    const detectedTimezone = getTimezone()
    displayTimezone = formatTimezoneForDisplay(detectedTimezone)
    validationTimezone = detectedTimezone

    if (tzLabelElement) {
      tzLabelElement.textContent = displayTimezone
    }
  }

  const refresh = () => {
    // Re-query the elements to ensure we have current references
    const currentDateInput = dialog.querySelector<HTMLInputElement>('input.js-discussion-due-date[type="date"]')
    const currentTimestampInput = dialog.querySelector<HTMLInputElement>('input.js-discussion-timestamp[type="hidden"]')

    const dateValue = currentDateInput?.value || ''
    const base = parsePickerValue(dateValue)
    const hourVal = hourSel.value
    const minVal = minuteSel.value
    const ampmVal = ampmSel.value

    if (base && hourVal && minVal) {
      // Combine the date with the selected time
      const h24 = convertTo24Hour(Number(hourVal), ampmVal)
      const mins = Number(minVal)
      base.setHours(h24, mins, 0, 0)

      // Update the hidden timestamp input with the combined datetime
      // This is what gets submitted to the server
      if (currentTimestampInput) {
        const isoDateTime = toLocalIso(base)
        currentTimestampInput.value = isoDateTime
      }
    } else if (currentTimestampInput) {
      // Clear the hidden input if date or time is incomplete
      currentTimestampInput.value = ''
    }

    // Update the summary display
    summary.textContent = buildSummary({
      date: base,
      hour: hourVal,
      minute: minVal,
      ampm: ampmVal,
      tzLabel: displayTimezone,
    })
  }

  const updateUI = () => {
    refresh()

    // Re-query for current value
    const currentDateInput = dialog.querySelector<HTMLInputElement>('input.js-discussion-due-date[type="date"]')
    const dateValue = currentDateInput?.value || ''

    const {ok: scheduleOk} = validateSchedule({
      date: parsePickerValue(dateValue),
      hour: hourSel.value,
      minute: minuteSel.value,
      ampm: ampmSel.value,
      now: new Date(),
    })

    scheduleBtn.disabled = !readyToSchedule({
      title: titleInput.value,
      body: bodyInput.value,
      scheduleOk,
      tzSet: tzIsSet(validationTimezone),
    })

    if (scheduleBtn.disabled) {
      scheduleBtn.setAttribute('aria-disabled', 'true')
    } else {
      scheduleBtn.removeAttribute('aria-disabled')
    }
  }

  // Add event listeners to all the inputs that can change
  const pickerEls = [hourSel, minuteSel, ampmSel]
  for (const el of pickerEls) el.addEventListener('change', updateUI)

  titleInput.addEventListener('input', updateUI)
  bodyInput.addEventListener('input', updateUI)

  // Listen to the date input changes
  if (dateInput) {
    dateInput.addEventListener('change', updateUI)
    dateInput.addEventListener('input', updateUI)
  }

  const handleClick = (e: Event) => {
    if (!tzIsSet(validationTimezone)) {
      e.preventDefault()
      return
    }

    const currentDateInput = dialog.querySelector<HTMLInputElement>('input.js-discussion-due-date[type="date"]')
    const dateValue = currentDateInput?.value || ''

    const {ok} = validateSchedule({
      date: parsePickerValue(dateValue),
      hour: hourSel.value,
      minute: minuteSel.value,
      ampm: ampmSel.value,
      now: new Date(),
    })

    if (!ok) e.preventDefault()
  }
  scheduleBtn.addEventListener('click', handleClick)

  // Initial UI update to set everything up
  updateUI()

  // Cleanup function
  return () => {
    for (const el of pickerEls) el.removeEventListener('change', updateUI)
    titleInput.removeEventListener('input', updateUI)
    bodyInput.removeEventListener('input', updateUI)
    if (dateInput) {
      dateInput.removeEventListener('change', updateUI)
      dateInput.removeEventListener('input', updateUI)
    }
  }
}

observe('#schedule-discussion-dialog', {
  constructor: HTMLElement,
  add(dialog) {
    let detach: (() => void) | undefined

    const toggle = () => {
      if (dialog.hasAttribute('open')) {
        detach = attachScheduler(dialog)
      } else {
        detach?.()
        detach = undefined
      }
    }

    dialog.addEventListener('toggle', toggle)
    if (dialog.hasAttribute('open')) toggle()
  },
})
